import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function AdminActivityLogs() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("");
  const [action, setAction] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const loadLogs = useCallback(async (params = {}) => {
    setLoading(true);
    try {
      const query = new URLSearchParams({ limit: "100", ...params });
      const res = await api.get(`/admin/activity-logs?${query.toString()}`);
      setLogs(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail || err.response?.data?.error) || "Failed to load logs");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  function applyFilters() {
    const params = {};
    if (entity.trim()) params.entity = entity.trim();
    if (action.trim()) params.action = action.trim();
    loadLogs(params);
  }

  function formatTime(iso) {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    } catch {
      return iso;
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Activity Logs</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Audit trail of admin actions ({total} total)
          </p>
        </div>
        <Button variant="outline" onClick={() => loadLogs()} data-testid="admin-logs-refresh">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-end bg-card border border-border rounded-xl p-4">
        <div className="flex-1 w-full space-y-1.5">
          <label className="text-xs text-muted-foreground">Entity</label>
          <Input
            value={entity}
            onChange={(e) => setEntity(e.target.value)}
            placeholder="e.g. product, order, user"
          />
        </div>
        <div className="flex-1 w-full space-y-1.5">
          <label className="text-xs text-muted-foreground">Action</label>
          <Input
            value={action}
            onChange={(e) => setAction(e.target.value)}
            placeholder="e.g. product_create"
          />
        </div>
        <Button onClick={applyFilters}>
          <Filter className="w-4 h-4 mr-2" /> Filter
        </Button>
      </div>

      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-muted-foreground">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">No activity logs yet.</div>
        ) : (
          <div className="divide-y divide-border">
            {logs.map((log) => (
              <div key={log.id} className="px-4 py-3 hover:bg-secondary/20 transition-colors">
                <button
                  className="w-full text-left"
                  onClick={() => setExpandedId(expandedId === log.id ? null : log.id)}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-sm truncate">{log.summary || log.action}</div>
                      <div className="text-xs text-muted-foreground mt-0.5">
                        {log.actorName || log.actorEmail || "System"}
                        {log.actorRole ? ` · ${log.actorRole}` : ""}
                        {log.entity ? ` · ${log.entity}` : ""}
                        {log.method ? ` · ${log.method}` : ""}
                        {log.statusCode ? ` · ${log.statusCode}` : ""}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {formatTime(log.createdAt)}
                    </div>
                  </div>
                </button>
                {expandedId === log.id && (
                  <div className="mt-3 rounded-lg bg-secondary/40 p-3 text-xs space-y-1.5 font-mono">
                    <div><span className="text-muted-foreground">Action:</span> {log.action}</div>
                    <div><span className="text-muted-foreground">Path:</span> {log.path || "—"}</div>
                    <div><span className="text-muted-foreground">IP:</span> {log.ip || "—"}</div>
                    <div><span className="text-muted-foreground">Entity ID:</span> {log.entityId || "—"}</div>
                    {log.details && (
                      <pre className="mt-2 overflow-x-auto whitespace-pre-wrap text-[11px] text-muted-foreground">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
